import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useNoIndex } from "../../hooks/usePageSeo";
import {
  getBookingPagePublic,
  sendBookingOtp,
  verifyBookingAndCreate,
} from "../../firebase";
import {
  CalendarBlank,
  CalendarCheck,
  Clock,
  User,
  Phone,
  ShieldCheck,
  Warning,
  CheckCircle,
  ArrowRight,
  Timer,
  ChatCircleDots,
  Stethoscope,
  CaretLeft,
} from "@phosphor-icons/react";
import ContentLoader from "../../components/shared/ContentLoader";
import LoaderLoop from "../../components/shared/loader/LoaderLoop";
import "./BookingPage.css";

// Stages of the self-booking flow
const STAGES = {
  LOADING: "loading",
  SERVICE: "service", // choose a service (only when the clinic published any)
  SELECT: "select", // choose a slot
  FORM: "form", // name + phone
  OTP: "otp", // verify SMS code
  SUCCESS: "success",
  UNAVAILABLE: "unavailable", // booking disabled / multi-therapist
  NOTFOUND: "notfound", // bad slug
  ERROR: "error",
};

// Build an "HH:MM - HH:MM" window from a start ISO + duration in minutes.
// Times are forced to Asia/Jerusalem and wrapped LTR so the range reads
// correctly inside the RTL layout.
function formatTimeWindow(startISO, durationMin) {
  const start = new Date(startISO);
  const opts = {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "Asia/Jerusalem",
  };
  const startStr = start.toLocaleTimeString("he-IL", opts);
  if (!durationMin) return startStr;
  const end = new Date(start.getTime() + durationMin * 60000);
  const endStr = end.toLocaleTimeString("he-IL", opts);
  return `${startStr} - ${endStr}`;
}

// Ordered steps shown in the progress bar. The service step only exists when
// the clinic published public services; otherwise the legacy 3-step flow.
const STEP_ORDER_BASE = [STAGES.SELECT, STAGES.FORM, STAGES.OTP];
const STEP_ORDER_WITH_SERVICE = [STAGES.SERVICE, ...STEP_ORDER_BASE];
const STEP_LABELS = { service: "שירות", select: "מועד", form: "פרטים", otp: "אימות" };

const getServices = (res) => (Array.isArray(res?.services) ? res.services : []);

function BookingPage() {
  const { slug } = useParams();
  useNoIndex();
  const [stage, setStage] = useState(STAGES.LOADING);
  // { clinic, therapistName, duration, days, services?, slotsByService? }
  const [data, setData] = useState(null);
  const [unavailableReason, setUnavailableReason] = useState(null);

  // Public service picked by the patient: { id, name, color, duration?, price? }
  const [selectedService, setSelectedService] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null); // { startISO, window, dayLabel, dateLabel }
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [formError, setFormError] = useState("");
  const [otpError, setOtpError] = useState("");
  // Shown on the slot / service picker after the chosen slot was taken (or the
  // service withdrawn) between selection and OTP — previously the page just
  // bounced back silently and the patient had no idea why.
  const [slotNotice, setSlotNotice] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [confirmed, setConfirmed] = useState(null); // { date, time }

  // Load booking page data on mount
  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const res = await getBookingPagePublic(slug);
        if (!active) return;
        if (!res?.found) {
          setStage(STAGES.NOTFOUND);
          return;
        }
        if (!res.available) {
          setUnavailableReason(res.reason || "disabled");
          setStage(STAGES.UNAVAILABLE);
          return;
        }
        setData(res);
        setStage(getServices(res).length > 0 ? STAGES.SERVICE : STAGES.SELECT);
      } catch (error) {
        console.error("[CK-6023] Error loading booking page:", {
          errorCode: "CK-6023",
          errorMessage: error.message,
          timestamp: new Date().toISOString(),
        });
        if (active) setStage(STAGES.ERROR);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [slug]);

  const services = getServices(data);
  const hasServices = services.length > 0;
  const stepOrder = hasServices ? STEP_ORDER_WITH_SERVICE : STEP_ORDER_BASE;
  // With a service picked, slots come from that service's duration. A service
  // whose duration is hidden (showDuration off) has no `duration` here, so the
  // time label shows the start time only.
  const activeDuration = selectedService
    ? selectedService.duration ?? null
    : data?.duration ?? null;
  const activeDays = selectedService
    ? data?.slotsByService?.[selectedService.id] || []
    : data?.days || [];

  const handleSelectService = (service) => {
    setSelectedService(service);
    setSelectedSlot(null);
    setFormError("");
    setStage(STAGES.SELECT);
  };

  const handleSelectSlot = (day, slot) => {
    setSelectedSlot({
      startISO: slot.startISO,
      window: formatTimeWindow(slot.startISO, activeDuration),
      dayLabel: day.dayLabel,
      dateLabel: day.dateLabel,
    });
    setFormError("");
    setSlotNotice("");
    setStage(STAGES.FORM);
  };

  // Re-fetch availability (slot taken / service withdrawn) and land on the
  // right stage, keeping the chosen service when it still exists.
  const reloadAvailability = async () => {
    setOtpError("");
    setStage(STAGES.LOADING);
    try {
      const fresh = await getBookingPagePublic(slug);
      setData(fresh);
      setSelectedSlot(null);
      const freshServices = getServices(fresh);
      const stillThere =
        selectedService && freshServices.find((sv) => sv.id === selectedService.id);
      if (stillThere) {
        setSelectedService(stillThere);
        setStage(STAGES.SELECT);
      } else {
        setSelectedService(null);
        setStage(freshServices.length > 0 ? STAGES.SERVICE : STAGES.SELECT);
      }
    } catch (error) {
      console.error("[CK-6025] Error reloading booking availability:", {
        errorCode: "CK-6025",
        errorMessage: error.message,
        timestamp: new Date().toISOString(),
      });
      setStage(STAGES.ERROR);
    }
  };

  const handleSendOtp = async () => {
    setFormError("");
    const trimmedName = name.trim();
    if (trimmedName.length < 2) {
      setFormError("נא להזין שם מלא");
      return;
    }
    if (phone.replace(/\D/g, "").length < 9) {
      setFormError("נא להזין מספר טלפון תקין");
      return;
    }
    setSubmitting(true);
    try {
      const res = await sendBookingOtp(slug, phone);
      if (res?.success) {
        setCode("");
        setOtpError("");
        setStage(STAGES.OTP);
      } else {
        setFormError(otpSendErrorMessage(res?.error));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    setOtpError("");
    setResending(true);
    try {
      const res = await sendBookingOtp(slug, phone);
      if (!res?.success) setOtpError(otpSendErrorMessage(res?.error));
    } finally {
      setResending(false);
    }
  };

  const handleVerify = async () => {
    setOtpError("");
    if (code.replace(/\D/g, "").length !== 6) {
      setOtpError("נא להזין קוד בן 6 ספרות");
      return;
    }
    setSubmitting(true);
    try {
      const res = await verifyBookingAndCreate(
        slug,
        phone,
        code.trim(),
        name.trim(),
        selectedSlot.startISO,
        selectedService?.id || null,
      );
      if (res?.success) {
        setConfirmed(res.appointment);
        setStage(STAGES.SUCCESS);
        return;
      }
      if (
        res?.error === "SLOT_TAKEN" ||
        res?.error === "SERVICE_UNAVAILABLE" ||
        res?.error === "THERAPIST_UNAVAILABLE"
      ) {
        // Slot booked by someone else / service withdrawn / therapist absence
        // added meanwhile — reload and tell the patient why they are back at
        // the picker.
        setSlotNotice(
          res.error === "SLOT_TAKEN"
            ? "המועד שבחרתם נתפס בינתיים. אנא בחרו מועד אחר."
            : res.error === "THERAPIST_UNAVAILABLE"
              ? "המטפל/ת אינו/ה זמין/ה במועד זה. אנא בחרו מועד אחר."
              : "השירות שבחרתם אינו זמין יותר. אנא בחרו שירות או מועד אחר."
        );
        await reloadAvailability();
        return;
      }
      setOtpError(otpVerifyErrorMessage(res?.error));
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render helpers ──────────────────────────────────────────────────────────

  const renderHeader = () =>
    data?.clinic ? (
      <div className="book-clinic">
        {data.clinic.logoUrl ? (
          <div className="book-logo-ring">
            <img
              className="book-logo"
              src={data.clinic.logoUrl}
              alt={data.clinic.name}
            />
          </div>
        ) : (
          <div className="book-logo-ring">
            <div className="book-logo book-logo--placeholder">
              <CalendarBlank size={28} weight="fill" />
            </div>
          </div>
        )}
        <h1>{data.clinic.name}</h1>
        {data.therapistName && (
          <p className="book-therapist">{data.therapistName}</p>
        )}
        {selectedService ? (
          <span className="book-duration-chip">
            <Stethoscope size={14} weight="fill" />
            {selectedService.name}
            {selectedService.duration ? ` · ${selectedService.duration} דקות` : ""}
          </span>
        ) : !hasServices && data.duration ? (
          <span className="book-duration-chip">
            <Timer size={14} weight="fill" />
            משך פגישה: {data.duration} דקות
          </span>
        ) : null}
      </div>
    ) : null;

  // Meta line under a service name: duration and/or price, only when the
  // therapist chose to expose them.
  const renderServiceMeta = (service) => {
    const parts = [];
    if (service.duration) {
      parts.push(
        <span key="d" className="book-service-meta-item">
          <Timer size={13} weight="fill" /> {service.duration} דקות
        </span>,
      );
    }
    if (service.price != null) {
      parts.push(
        <span key="p" className="book-service-meta-item book-service-price" dir="ltr">
          ₪{service.price}
        </span>,
      );
    }
    return parts.length ? <span className="book-service-meta">{parts}</span> : null;
  };

  // Progress bar for the booking steps (hidden on info/success states)
  const renderSteps = (current) => {
    const currentIdx = stepOrder.indexOf(current);
    return (
      <div className="book-steps" aria-hidden="true">
        {stepOrder.map((step, i) => (
          <div
            key={step}
            className={`book-step ${
              i < currentIdx
                ? "book-step--done"
                : i === currentIdx
                  ? "book-step--active"
                  : ""
            }`}
          >
            <span className="book-step-dot">
              {i < currentIdx ? (
                <CheckCircle size={14} weight="fill" />
              ) : (
                i + 1
              )}
            </span>
            <span className="book-step-label">{STEP_LABELS[step]}</span>
            {i < stepOrder.length - 1 && <span className="book-step-line" />}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="book-page">
      <div className="book-container">
        {stage === STAGES.LOADING && (
          <div className="book-state book-state--loading">
            <ContentLoader message="טוען מועדים פנויים..." />
          </div>
        )}

        {stage === STAGES.NOTFOUND && (
          <div className="book-state book-anim-in">
            <div className="book-state-icon book-state-icon--warn">
              <Warning size={40} weight="fill" />
            </div>
            <h1>הקישור לא נמצא</h1>
            <p>ייתכן שהקישור שגוי או שאינו פעיל יותר.</p>
          </div>
        )}

        {stage === STAGES.UNAVAILABLE && (
          <div className="book-state book-anim-in">
            <div className="book-state-icon book-state-icon--warn">
              <Warning size={40} weight="fill" />
            </div>
            <h1>הזמנת תורים אינה זמינה</h1>
            <p>
              {unavailableReason === "not_solo"
                ? "הזמנה עצמית אינה זמינה כרגע בקליניקה זו. אנא צרו קשר ישירות."
                : "הזמנה עצמית אינה פעילה כרגע. אנא צרו קשר ישירות."}
            </p>
          </div>
        )}

        {stage === STAGES.ERROR && (
          <div className="book-state book-anim-in">
            <div className="book-state-icon book-state-icon--warn">
              <Warning size={40} weight="fill" />
            </div>
            <h1>אירעה שגיאה</h1>
            <p>לא ניתן לטעון את העמוד כעת. נסו שוב מאוחר יותר.</p>
          </div>
        )}

        {stage === STAGES.SERVICE && (
          <div className="book-anim-in" key="service">
            {renderHeader()}
            {renderSteps(STAGES.SERVICE)}
            {slotNotice && (
              <p className="book-slot-notice" role="status">{slotNotice}</p>
            )}
            <div className="book-section-title">
              <Stethoscope size={18} weight="bold" />
              <span>בחרו שירות</span>
            </div>
            <p className="book-section-desc">
              בחרו את סוג הטיפול שתרצו לקבוע; המועדים הפנויים יותאמו למשך השירות.
            </p>
            <div className="book-services">
              {services.map((service, idx) => (
                <button
                  key={service.id}
                  type="button"
                  className="book-service book-anim-up"
                  style={{ animationDelay: `${Math.min(idx * 60, 300)}ms` }}
                  onClick={() => handleSelectService(service)}
                >
                  <span
                    className="book-service-dot"
                    style={{ backgroundColor: service.color || "var(--book-primary)" }}
                  />
                  <span className="book-service-text">
                    <span className="book-service-name">{service.name}</span>
                    {renderServiceMeta(service)}
                  </span>
                  <CaretLeft size={16} weight="bold" className="book-service-caret" />
                </button>
              ))}
            </div>
          </div>
        )}

        {stage === STAGES.SELECT && (
          <div className="book-anim-in" key="select">
            {renderHeader()}
            {renderSteps(STAGES.SELECT)}
            {slotNotice && (
              <p className="book-slot-notice" role="status">{slotNotice}</p>
            )}
            {hasServices && (
              <button
                className="book-back"
                onClick={() => setStage(STAGES.SERVICE)}
              >
                <ArrowRight size={16} weight="bold" />
                <span>שינוי שירות</span>
              </button>
            )}
            <div className="book-section-title">
              <CalendarBlank size={18} weight="bold" />
              <span>בחרו מועד לפגישה</span>
            </div>
            <p className="book-section-desc">
              לחצו על שעה פנויה כדי להמשיך. המועד יישמר עבורכם עד לסיום התהליך.
            </p>
            {activeDays.length === 0 ? (
              <div className="book-empty">
                <div className="book-state-icon book-state-icon--muted">
                  <CalendarBlank size={32} weight="fill" />
                </div>
                <p>אין מועדים פנויים כרגע. נסו שוב בהמשך.</p>
              </div>
            ) : (
              <div className="book-days">
                {activeDays.map((day, dayIdx) => (
                  <div
                    key={day.date}
                    className="book-day book-anim-up"
                    style={{ animationDelay: `${Math.min(dayIdx * 70, 350)}ms` }}
                  >
                    <div className="book-day-head">
                      <span className="book-day-name">{day.dayLabel}׳</span>
                      <span className="book-day-date">{day.dateLabel}</span>
                      <span className="book-day-count">
                        {day.slots.length} מועדים
                      </span>
                    </div>
                    <div className="book-slots">
                      {day.slots.map((slot) => (
                        <button
                          key={slot.startISO}
                          className="book-slot"
                          onClick={() => handleSelectSlot(day, slot)}
                        >
                          <Clock
                            size={15}
                            weight="bold"
                            className="book-slot-icon"
                          />
                          <span className="book-slot-time" dir="ltr">
                            {formatTimeWindow(slot.startISO, activeDuration)}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {stage === STAGES.FORM && (
          <div className="book-anim-in" key="form">
            {renderHeader()}
            {renderSteps(STAGES.FORM)}
            <button
              className="book-back"
              onClick={() => setStage(STAGES.SELECT)}
            >
              <ArrowRight size={16} weight="bold" />
              <span>חזרה למועדים</span>
            </button>

            <div className="book-selected">
              <div className="book-selected-icon">
                <CalendarCheck size={20} weight="fill" />
              </div>
              <div className="book-selected-text">
                {selectedService && (
                  <span className="book-selected-service">{selectedService.name}</span>
                )}
                <span className="book-selected-day">
                  יום {selectedSlot.dayLabel}׳ {selectedSlot.dateLabel}
                </span>
                <span className="book-selected-time" dir="ltr">
                  {selectedSlot.window}
                </span>
              </div>
            </div>

            <p className="book-section-desc">
              כמעט סיימנו! מלאו את הפרטים ונשלח לכם קוד אימות קצר ב-SMS.
            </p>

            <div className="book-field">
              <label className="book-label">
                <User size={16} weight="bold" /> שם מלא
              </label>
              <input
                className="book-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="ישראל ישראלי"
              />
            </div>

            <div className="book-field">
              <label className="book-label">
                <Phone size={16} weight="bold" /> טלפון נייד
              </label>
              <input
                className="book-input"
                type="tel"
                inputMode="numeric"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="050-0000000"
              />
            </div>

            {formError && <p className="book-error-text">{formError}</p>}

            <button
              className="book-btn-primary"
              onClick={handleSendOtp}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <LoaderLoop size="xs" />
                  <span>שולח קוד...</span>
                </>
              ) : (
                "שליחת קוד אימות"
              )}
            </button>
            <p className="book-hint">
              <ShieldCheck size={14} weight="fill" /> נשלח קוד אימות ב-SMS
              למספר שהזנת
            </p>
          </div>
        )}

        {stage === STAGES.OTP && (
          <div className="book-anim-in" key="otp">
            {renderHeader()}
            {renderSteps(STAGES.OTP)}
            <button className="book-back" onClick={() => setStage(STAGES.FORM)}>
              <ArrowRight size={16} weight="bold" />
              <span>חזרה</span>
            </button>
            <div className="book-otp-hero">
              <div className="book-otp-hero-icon">
                <ChatCircleDots size={26} weight="fill" />
              </div>
              <h2>אימות טלפון</h2>
              <p className="book-otp-desc">
                שלחנו קוד בן 6 ספרות ב-SMS אל{" "}
                <span dir="ltr" className="book-otp-phone">
                  {phone}
                </span>
              </p>
            </div>

            <input
              className="book-input book-otp-input"
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) =>
                setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              placeholder="------"
              autoFocus
            />

            {otpError && <p className="book-error-text">{otpError}</p>}

            <button
              className="book-btn-primary"
              onClick={handleVerify}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <LoaderLoop size="xs" />
                  <span>מאמת...</span>
                </>
              ) : (
                "אישור וקביעת הפגישה"
              )}
            </button>

            <button
              className="book-link-btn"
              onClick={handleResend}
              disabled={resending}
            >
              {resending ? "שולח..." : "לא קיבלתי קוד — שליחה חוזרת"}
            </button>
          </div>
        )}

        {stage === STAGES.SUCCESS && (
          <div className="book-state book-anim-in" key="success">
            <div className="book-state-icon book-state-icon--success book-anim-pop">
              <CheckCircle size={44} weight="fill" />
            </div>
            <h1>הבקשה נשלחה!</h1>
            {(selectedSlot || confirmed) && (
              <div className="book-success-card book-anim-up">
                <CalendarCheck size={20} weight="fill" />
                <span>
                  {selectedService ? `${selectedService.name} · ` : ""}
                  {selectedSlot ? (
                    <>
                      יום {selectedSlot.dayLabel}׳ {selectedSlot.dateLabel}
                      {" · "}
                      <span dir="ltr">{selectedSlot.window}</span>
                    </>
                  ) : (
                    <>
                      {confirmed.date} בשעה {confirmed.time}
                    </>
                  )}
                </span>
              </div>
            )}
            <p>
              התור שלך ממתין לאישור המטפל.
              <br />
              נעדכן אותך ב-SMS מיד לאחר האישור — אין צורך לעשות דבר נוסף.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// User-facing messages for OTP send failures
function otpSendErrorMessage(error) {
  switch (error) {
    case "COOLDOWN":
      return "כבר נשלח קוד. המתינו רגע לפני שליחה חוזרת.";
    case "INVALID_PHONE":
      return "מספר הטלפון אינו תקין.";
    case "SMS_QUOTA":
      return "לא ניתן לשלוח קוד כרגע. אנא צרו קשר עם הקליניקה.";
    case "RATE_LIMITED":
      return "נשלחו יותר מדי בקשות. נסו שוב מאוחר יותר.";
    case "NOT_AVAILABLE":
      return "הזמנה עצמית אינה זמינה כעת.";
    default:
      return "שליחת הקוד נכשלה. נסו שוב.";
  }
}

// User-facing messages for OTP verification failures
function otpVerifyErrorMessage(error) {
  switch (error) {
    case "INVALID_CODE":
      return "הקוד שגוי. נסו שוב.";
    case "CODE_EXPIRED":
      return "הקוד פג תוקף. בקשו קוד חדש.";
    case "TOO_MANY_ATTEMPTS":
      return "יותר מדי ניסיונות. בקשו קוד חדש.";
    case "SERVICE_UNAVAILABLE":
      return "השירות שנבחר אינו זמין יותר. בחרו שירות אחר.";
    default:
      return "האימות נכשל. נסו שוב.";
  }
}

export default BookingPage;
